import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Workspace } from "@/hooks/useWorkspace";
import type { MemberRow } from "./useWorkspaceMembers";

type Props = {
  workspace: Workspace;
  target: MemberRow | null;
  displayMap: Record<string, { display_name: string | null; email: string | null }>;
  onClose: () => void;
  onConfirm: (newOwnerId: string) => Promise<boolean>;
};

export const TransferOwnershipDialog = ({ workspace, target, displayMap, onClose, onConfirm }: Props) => {
  const [transferText, setTransferText] = useState("");
  const [transferring, setTransferring] = useState(false);
  const expectedConfirm = (workspace.workspace_label ?? workspace.display_name ?? "workspace").trim();

  const handleClose = () => {
    setTransferText("");
    onClose();
  };

  const run = async () => {
    if (!target) return;
    setTransferring(true);
    const ok = await onConfirm(target.member_user_id);
    setTransferring(false);
    if (ok) handleClose();
  };

  return (
    <AlertDialog open={!!target} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transfer ownership?</AlertDialogTitle>
          <AlertDialogDescription>
            {target && (
              <>
                You'll hand <span className="font-medium text-foreground">{expectedConfirm}</span> over to{" "}
                <span className="font-medium text-foreground">
                  {displayMap[target.member_user_id]?.display_name ||
                    displayMap[target.member_user_id]?.email ||
                    "this member"}
                </span>.
                You'll keep access as an Owner / admin member; they'll become the new owner and gain billing-style
                controls (rename, delete, transfer again). This can't be undone without their cooperation.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="transfer-confirm" className="text-sm">
            Type <span className="font-mono font-semibold text-foreground">{expectedConfirm}</span> to confirm
          </Label>
          <Input
            id="transfer-confirm"
            value={transferText}
            onChange={(e) => setTransferText(e.target.value)}
            placeholder={expectedConfirm}
            autoComplete="off"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={transferring}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); run(); }}
            disabled={transferring || transferText.trim() !== expectedConfirm}
          >
            {transferring ? "Transferring…" : "Transfer ownership"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
